/**
 * K-T-13-C — Layer-3 hook for canonical completed-ticket aggregate
 * efficiency.
 *
 * Thin wrapper around `kpi_completed_ticket_efficiency(shop_id,
 * start_at, end_at, tech_id, customer_search)`. Returns the canonical
 * SUM(predicted_minutes) / SUM(actual_minutes) ratio over the panel's
 * filter set, with the standard `MIN_KPI_SAMPLE_SIZE = 3` sample gate.
 *
 * Replaces the mean-of-per-ticket-ratios `avgEfficiency` /
 * `avgEfficiencySelectedDate` calculations in
 * `src/pages/CompletedTickets.tsx` and
 * `src/components/dashboard/CompletedTicketsSection.tsx`.
 *
 * The K-T-4 `useTechnicianEfficiency` hook was evaluated for reuse but
 * is structurally incompatible — it requires a non-null tech_id and
 * has no customer-search support. K-T-13-C is the minimal canonical
 * primitive that fits both completed-ticket pages.
 *
 * Date-range conversion: shop-local `yyyy-MM-dd` strings → UTC ISO
 * timestamps via `fromZonedTime`, mirroring K-T-10/11/12/13-B.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { fromZonedTime } from 'date-fns-tz';
import type { CompletedTicketEfficiencyResult } from '../../../supabase/functions/_shared/kpiCanonical';

export interface UseCompletedTicketEfficiencyOptions {
  shopId: string;
  /** Shop-local `yyyy-MM-dd`. */
  startDate: string;
  /** Shop-local `yyyy-MM-dd`. */
  endDate: string;
  shopTimezone: string;
  /**
   * Tech filter. Pass the panel's `'all'` value or `null`/`undefined`
   * to include every technician. Pass a UUID to scope to one tech.
   */
  technicianId?: string | null;
  /**
   * Customer-name search. Pass `null`/`undefined`/empty string to
   * skip the filter; pass a non-empty value to ILIKE-match
   * `customer_name`.
   */
  customerSearch?: string | null;
}

export interface UseCompletedTicketEfficiencyResult {
  data: CompletedTicketEfficiencyResult | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * Stable query key:
 *   ['kpi', 'kpi_completed_ticket_efficiency', shopId, startDate,
 *    endDate, shopTimezone, normalizedTechId, normalizedCustomerSearch]
 *
 * `'all'` and empty/undefined customer searches normalize to `null` so
 * the cache key is stable across UI representations of "no filter".
 */
export function useCompletedTicketEfficiency(
  opts: UseCompletedTicketEfficiencyOptions,
): UseCompletedTicketEfficiencyResult {
  const normalizedTechId =
    !opts.technicianId || opts.technicianId === 'all'
      ? null
      : opts.technicianId;
  const normalizedCustomerSearch =
    !opts.customerSearch || opts.customerSearch.trim() === ''
      ? null
      : opts.customerSearch;

  const query = useQuery({
    queryKey: [
      'kpi',
      'kpi_completed_ticket_efficiency',
      opts.shopId,
      opts.startDate,
      opts.endDate,
      opts.shopTimezone,
      normalizedTechId,
      normalizedCustomerSearch,
    ],
    queryFn: async (): Promise<CompletedTicketEfficiencyResult | null> => {
      const startAt = fromZonedTime(
        `${opts.startDate}T00:00:00`,
        opts.shopTimezone,
      ).toISOString();
      const endAt = fromZonedTime(
        `${opts.endDate}T23:59:59.999`,
        opts.shopTimezone,
      ).toISOString();

      const { data, error } = await (supabase.rpc as unknown as (
        fn: string,
        args: {
          p_shop_id: string;
          p_start_at: string;
          p_end_at: string;
          p_tech_id: string | null;
          p_customer_search: string | null;
        },
      ) => Promise<{
        data: CompletedTicketEfficiencyResult[] | null;
        error: unknown;
      }>)('kpi_completed_ticket_efficiency', {
        p_shop_id: opts.shopId,
        p_start_at: startAt,
        p_end_at: endAt,
        p_tech_id: normalizedTechId,
        p_customer_search: normalizedCustomerSearch,
      });

      if (error) throw error;
      return Array.isArray(data) && data.length > 0 ? data[0] : null;
    },
    enabled: Boolean(
      opts.shopId &&
        opts.startDate &&
        opts.endDate &&
        opts.shopTimezone,
    ),
    staleTime: 60 * 1000,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
